import { faChevronRight, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import QuestionStatus from '../partials/QuestionStatus';

import styles from '../../../styles/items/question.module.scss';

interface QuestionItemProps {
    className?: string;
    participationEnded?: boolean;
    question?: Question;
    answer?: ParticipationAnswer;
    showAnswer?: boolean;
    disabled?: boolean;
    url?: string;
    onClick?: () => void;
}

function QuestionItem({
    className,
    participationEnded = false,
    question,
    answer = null,
    showAnswer = true,
    disabled = false,
    url = null,
    onClick = null,
}: QuestionItemProps) {
    const themeClassName = useThemeClassName(styles);
    const {
        body,
        canAnswer = false,
        expired = false,
        points,
        validAnswer = null,
        index: count = 0,
        choices: questionChoices = [],
    } = question || {};

    const { valid = false, value: answerValue = null } = answer || {};
    const hasValidAnswer = validAnswer !== null;

    const { label: validAnswerLabel = null } =
        questionChoices.find(({ id }) => id === validAnswer) || {};

    const countString = useMemo(() => `${count + 1}`.padStart(2, '0'), [count]);
    const locked = !canAnswer && !expired;
    const showPoints = valid === true;

    const userChoice =
        (questionChoices || []).find(({ id: choiceId }) => choiceId === answerValue) || null;

    const { label: answerLabel = null } = userChoice || {};

    return (
        <Button
            className={classNames([
                styles.container,
                {
                    [styles.locked]: locked,
                    [styles.hasValidAnswer]: hasValidAnswer,
                    [styles.valid]: valid,
                },
                themeClassName,
                className,
            ])}
            disabled={disabled}
            onClick={onClick}
            href={url}
        >
            <span className={styles.content}>
                <span className={styles.inner}>
                    <span className={styles.label}>
                        <span className={styles.count}>
                            <FormattedMessage
                                defaultMessage="Question {count}"
                                description="NHL question count label"
                                values={{
                                    count: countString,
                                }}
                            />
                        </span>
                        <QuestionStatus
                            className={styles.status}
                            question={question}
                            answer={answer}
                            ended={participationEnded}
                        />
                        {showPoints ? (
                            <span className={styles.pointsInline}>
                                <FormattedMessage
                                    defaultMessage="{count, plural, =0 {# pt} one {# pt} other {# pts}}"
                                    description="pts label"
                                    values={{ count: points || 0 }}
                                />
                            </span>
                        ) : null}
                    </span>
                    <h2 className={styles.body}>{body}</h2>
                    {showAnswer && answerLabel !== null ? (
                        <>
                            {validAnswerLabel !== null && !valid ? (
                                <h2 className={classNames([styles.body, styles.answer])}>
                                    <FormattedMessage
                                        defaultMessage="Your answer:"
                                        description="answer label"
                                    />{' '}
                                    {validAnswerLabel}
                                </h2>
                            ) : null}
                            <h2
                                className={classNames([
                                    styles.body,
                                    styles.answer,
                                    {
                                        [styles.invalid]: validAnswer !== null && !valid,
                                    },
                                ])}
                            >
                                {valid !== null && !valid ? (
                                    <FormattedMessage
                                        defaultMessage="Correct answer:"
                                        description="answer label"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Your answer:"
                                        description="answer label"
                                    />
                                )}{' '}
                                {answerLabel}
                            </h2>
                        </>
                    ) : null}
                </span>
                <span className={styles.icon}>
                    <FontAwesomeIcon icon={locked ? faLock : faChevronRight} />
                </span>
            </span>
            {showPoints ? (
                <span className={styles.pointsAbsolute}>
                    <FormattedMessage
                        defaultMessage="{count, plural, =0 {# pt} one {# pt} other {# pts}}"
                        description="pts label"
                        values={{ count: points || 0 }}
                    />
                </span>
            ) : null}
        </Button>
    );
}

export default QuestionItem;
